import React, { useEffect, useState } from "react";
import { Checkbox } from "@mui/material";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import config from "../../config.json";
import { useNavigate } from "react-router-dom";

export default function RegisterFeatures({ organizationDetails }) {
  const navigate = useNavigate();
  const [allFeatures, setAllFeatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [selectedCategories, setSelectedCategories] = useState({});

  // Define mandatory categories
  const mandatoryCategories = ["vehicle", "stop", "route"];
  
  const predefinedCategories = [
    "vehicle",
    "stop",
    "route",
    "schedule",
    "site",
    "driver",
    "tracking247",
    "alerts",
    "admin",
  ];

  useEffect(() => {
    getAllTheAvailableFeatures();
  }, []);

  useEffect(() => {
    if (allFeatures.length > 0) {
      let apiCategories = Array.from(
        new Set(allFeatures.map((feature) => {
          const pathParts = feature.api_path.split("/");
          return pathParts[2]; // e.g., 'vehicle' from '/api/vehicle/getall'
        }))
      ).filter((category) => predefinedCategories.includes(category));

      const finalCategories = [...predefinedCategories];
      const initialCategoryStates = {};
      finalCategories.forEach((category) => {
        // Mark mandatory categories as selected by default
        initialCategoryStates[category] = mandatoryCategories.includes(category);
      });

      setCategories(finalCategories);
      setSelectedCategories(initialCategoryStates);
      
      // Also mark all features from mandatory categories as selected
      const initialFeatureStates = {};
      mandatoryCategories.forEach(category => {
        const categoryFeatures = allFeatures.filter(feature => {
          const pathParts = feature.api_path.split("/");
          return pathParts[2] === category && feature.isDefault === true;
        });
        
        categoryFeatures.forEach(feature => {
          initialFeatureStates[feature.id] = true;
        });
      });
      
      setSelectedFeatures(initialFeatureStates);
      setLoading(false);
    } else {
      const initialCategoryStates = {};
      predefinedCategories.forEach((category) => {
        initialCategoryStates[category] = mandatoryCategories.includes(category);
      });

      setCategories(predefinedCategories);
      setSelectedCategories(initialCategoryStates);
      setLoading(false);
    }
  }, [allFeatures]);

  useEffect(() => {
    const selectedIds = getSelectedFeatureIds();
    console.log("Current selected feature IDs (state change):", selectedIds);
  }, [selectedFeatures, selectedCategories]);

  const getAllTheAvailableFeatures = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/feature/`, {
        withCredentials: true,
      });
      setAllFeatures(response.data);
      console.log("Fetched features:", response.data);
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("Unauthorized request");
        setError("Unauthorized request");
      } else {
        console.error("Error fetching available features:", error);
        setError("Failed to load features");
      }
      setLoading(false);
    }
  };

  const getFeaturesByCategory = (category) => {
    const categoryFeatures = allFeatures.filter((feature) => {
      const pathParts = feature.api_path.split("/");
      const basePath = pathParts[2];
      if (category === "admin") {
        return basePath === "user" || basePath === "role";
      }
      if (category === "alerts") {
        return basePath === "alert" || (basePath === "vehicle" && pathParts[3] === "alert");
      }
      return basePath === category;
    });

    // No grouping; keep each feature separate
    return categoryFeatures;
  };

  const handleCategoryChange = (category) => {
    // Don't allow deselecting mandatory categories
    if (mandatoryCategories.includes(category)) {
      return;
    }
    
    const newState = !selectedCategories[category];
    setSelectedCategories((prev) => ({
      ...prev,
      [category]: newState,
    }));

    setSelectedFeatures((prev) => {
      const updatedFeatures = { ...prev };
      const categoryFeatures = getFeaturesByCategory(category);
      const regularFeatures = categoryFeatures.filter(
        (feature) => feature.isDefault === true
      );
      regularFeatures.forEach((feature) => {
        if (newState) {
          updatedFeatures[feature.id] = true;
        } else {
          if (feature.isDefault === true) {
            delete updatedFeatures[feature.id];
          }
        }
      });
      return updatedFeatures;
    });
  };

  const handleFeatureChange = (featureId) => {
    setSelectedFeatures((prev) => ({
      ...prev,
      [featureId]: !prev[featureId],
    }));
  };

  const getSelectedFeatureIds = () => {
    const selectedIds = new Set();

    categories.forEach((category) => {
      if (selectedCategories[category]) {
        const categoryFeatures = getFeaturesByCategory(category);
        const regularFeatures = categoryFeatures.filter(
          (feature) => feature.isDefault === true
        );
        regularFeatures.forEach((feature) => {
          selectedIds.add(feature.id);
        });
      }
    });

    Object.keys(selectedFeatures).forEach((id) => {
      if (selectedFeatures[id]) {
        selectedIds.add(parseInt(id));
      }
    });

    return Array.from(selectedIds);
  };

  const handleSave = async () => {
    const selectedIds = getSelectedFeatureIds();
    console.log("Selected feature IDs on save:", selectedIds);

    try {
      const organizationId = localStorage.getItem("registerOrganizationId");
      if (!organizationId) {
        throw new Error("Organization ID not found. Please register first.");
      }

      const response = await axios.put(
        `${config.baseURL}/register/${organizationId}`,
        {
          requestedFeatureIds: selectedIds,
        },
        { withCredentials: true }
      );

      console.log("Draft saved:", response.data);
      // alert("Draft saved successfully!");
    } catch (error) {
      console.error("Error saving draft:", error);
      setError(error.response?.data?.msg || "Failed to save draft");
    }
  };

  const handleTellUsMore = async () => {
    const selectedIds = getSelectedFeatureIds();
    console.log("Selected feature IDs before redirect:", selectedIds);

    try {
      const organizationId = localStorage.getItem("registerOrganizationId");
      if (!organizationId) {
        throw new Error("Organization ID not found. Please register first.");
      }

      const response = await axios.put(
        `${config.baseURL}/register/${organizationId}`,
        {
          requestedFeatureIds: selectedIds,
        },
        { withCredentials: true }
      );

      console.log("Features updated:", response.data);
      navigate("/registerneedinfo");
    } catch (error) {
      console.error("Error updating features:", error);
      setError(error.response?.data?.msg || "Failed to update features");
    }
  };

  useEffect(() => {
    const organizationId = localStorage.getItem("registerOrganizationId");
    if (!organizationId) {
      navigate("/register");
    }
    getAllTheAvailableFeatures();
  }, [navigate]);

  const getCategoryDisplayName = (category) => {
    if (category === "tracking247") return "24/7 Tracking";
    if (category === "alerts") return "Alerts & Notifications";
    if (category === "admin") return "Admin Panel";
    return category.charAt(0).toUpperCase() + category.slice(1) + " Management";
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading features...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "error.main" }}>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#F2F2F3", padding: "20px 0" }}>
      <Paper
        sx={{
          borderRadius: "8px",
          overflow: "hidden",
          margin: "0 40px",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 2px 5px 0px #00000040",
        }}
      >
        <Box sx={{ padding: "20px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 600,
                    fontSize: "25px",
                    color: "#183059",
                  }}
                >
                  Select Features For Your Requirement
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: "15px",
                    color: "#1F242E",
                  }}
                >
                  Choose The Features That Fit Your Needs
                </Typography>
                <Typography
                  variant="body2"
                  color="error"
                  gutterBottom
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    fontStyle: "italic",
                    color: "#FF0000",
                  }}
                >
                  <b style={{ fontWeight: 600, fontStyle: "normal" }}>Note:</b>{" "}
                  These Features Can't Be Changed Later, So Choose Details
                  Carefully.
                </Typography>
              </Box>
            </Grid>

            {categories.map((category) => {
              const categoryFeatures = getFeaturesByCategory(category);
              const regularFeatures = categoryFeatures.filter(
                (feature) => feature.isDefault === true
              );
              const addOnFeatures = categoryFeatures.filter(
                (feature) => feature.isDefault === false
              );

              if (
                categoryFeatures.length === 0 &&
                !["site", "driver", "tracking247"].includes(category)
              ) {
                return null;
              }

              const isMandatory = mandatoryCategories.includes(category);

              return (
                <Grid item xs={12} sm={6} md={4} key={category}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: "#FFFFFF",
                      minHeight: "150px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        padding: "4px",
                        backgroundColor : "#B7BFCC",
                        borderRadius: "8px",
                      }}
                    >
                      {!isMandatory && (
                        <Checkbox
                          checked={selectedCategories[category] || false}
                          onChange={() => handleCategoryChange(category)}
                          disabled={
                            regularFeatures.length === 0 &&
                            addOnFeatures.length === 0
                          }
                          sx={{
                            "&.Mui-checked": { color: "#20A8E0" },
                          }}
                        />
                      )}
                      <Typography
                        variant="subtitle1"
                        sx={{ 
                          color: "#111214", 
                          fontWeight: 700,
                          padding: isMandatory ? "8px  20px" : "0px" 
                        }}
                      >
                        {getCategoryDisplayName(category)}
                    
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 2 }}>
                      {regularFeatures.map((feature) => (
                        <Typography
                          key={feature.id}
                          variant="body2"
                          sx={{
                            mb: 1,
                            color: "#111214",
                            fontWeight: isMandatory || selectedFeatures[feature.id] ? 500 : 400,
                          }}
                        >
                          {feature.name}
                        </Typography>
                      ))}
                      {categoryFeatures.length === 0 && (
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: "italic", color: "#666" }}
                        >
                          No features available
                        </Typography>
                      )}
                      {addOnFeatures.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              backgroundColor: "#E8EAEE",
                              p: 1,
                              borderRadius: "4px",
                              boxShadow: "-2px 4px 5px 0px #00000040",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, color: "#111214" }}
                            >
                              Add-Ons
                            </Typography>
                          </Box>
                          {addOnFeatures.map((addOn) => (
                            <Box
                              key={addOn.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 1,
                              }}
                            >
                              <Checkbox
                                checked={selectedFeatures[addOn.id] || false}
                                onChange={() => handleFeatureChange(addOn.id)}
                                sx={{ "&.Mui-checked": { color: "#20A8E0" } }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "#666" }}
                              >
                                {addOn.displayName}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}

            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  sx={{
                    width: "240px",
                    height: "55px",
                    borderRadius: "8px",
                    background: "#FFFFFF",
                    border: "1px solid #414958",
                    color: "#111214",
                    textTransform: "none",
                  }}
                  onClick={handleSave}
                >
                  Save Draft
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    width: "240px",
                    height: "55px",
                    borderRadius: "8px",
                    background: "#F9E719",
                    border: "1px solid #414958",
                    color: "#111214",
                    "&:hover": { background: "#F9E719" },
                    textTransform: "none",
                  }}
                  onClick={handleTellUsMore}
                >
                  Tell us more...
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}